import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, MessageSquare, Heart, Crown, Medal } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState({ top_chatters: [], top_donors: [] });
  const [activeTab, setActiveTab] = useState('chatters');
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leaderboard`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (err) {
      console.log('Failed to fetch leaderboard');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLeaderboard();
    // Refresh every 5 minutes
    const interval = setInterval(fetchLeaderboard, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="w-4 h-4" style={{ color: '#FFD700' }} />;
    if (index === 1) return <Medal className="w-4 h-4" style={{ color: '#C0C0C0' }} />;
    if (index === 2) return <Medal className="w-4 h-4" style={{ color: '#CD7F32' }} />;
    return <span className="w-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>{index + 1}</span>;
  };

  const getRankBg = (index) => {
    if (index === 0) return 'rgba(255, 215, 0, 0.1)';
    if (index === 1) return 'rgba(192, 192, 192, 0.1)';
    if (index === 2) return 'rgba(205, 127, 50, 0.1)';
    return 'transparent';
  };

  return (
    <div 
      className="glass rounded-2xl p-4"
      data-testid="leaderboard"
    >
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5" style={{ color: '#FFD700' }} />
        <h3 
          className="text-lg font-bold"
          style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
        >
          Leaderboard
        </h3>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('chatters')}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'chatters' ? 'var(--primary)' : 'var(--surface-highlight)',
            color: activeTab === 'chatters' ? 'white' : 'var(--text-muted)'
          }}
        >
          <MessageSquare className="w-3 h-3" />
          Top Chatters
        </button>
        <button
          onClick={() => setActiveTab('donors')}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'donors' ? 'var(--primary)' : 'var(--surface-highlight)',
            color: activeTab === 'donors' ? 'white' : 'var(--text-muted)'
          }}
        >
          <Heart className="w-3 h-3" />
          Top Donors
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {activeTab === 'chatters' && (
          leaderboard.top_chatters.length > 0 ? (
            leaderboard.top_chatters.map((user, index) => (
              <div
                key={user.user_id || index}
                className="flex items-center gap-3 p-2 rounded-lg"
                style={{ backgroundColor: getRankBg(index) }}
              >
                {getRankIcon(index)}
                <span 
                  className="flex-1 text-sm truncate"
                  style={{ color: 'var(--text-main)' }}
                >
                  {user.name}
                </span>
                <span 
                  className="text-xs font-medium"
                  style={{ color: 'var(--primary)' }}
                >
                  {user.messages} msgs
                </span>
              </div>
            ))
          ) : (
            <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>
              No chat activity yet
            </p>
          )
        )}

        {activeTab === 'donors' && (
          leaderboard.top_donors.length > 0 ? (
            leaderboard.top_donors.map((donor, index) => (
              <div
                key={donor.user_id || index}
                className="flex items-center gap-3 p-2 rounded-lg"
                style={{ backgroundColor: getRankBg(index) }}
              >
                {getRankIcon(index)}
                <span 
                  className="flex-1 text-sm truncate"
                  style={{ color: 'var(--text-main)' }}
                >
                  {donor.name}
                </span>
                <span 
                  className="text-xs font-medium"
                  style={{ color: '#22C55E' }}
                >
                  ${donor.total.toFixed(0)}
                </span>
              </div>
            ))
          ) : (
            <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>
              No donations yet
            </p>
          )
        )}
      </div>
    </div>
  );
}
